import { useEffect } from 'react';
import '../css/CourseworkPage.css';
import ContactSection from './ContactSection';
import CoursePipeline from './CoursePipeline';
import { applyPageSeo, resetSeo } from '../seo';

const SEO_BY_PATH = {
    '/coursework/unix-shell': 'unix-shell',
    '/coursework/ai-search-agents': 'ai-search-agents',
};

function CourseworkPage({
    path,
    shortTitle,
    kicker,
    label,
    title,
    lede,
    meta = [],
    paragraphs,
    pipeline = [],
    pipelineLabel,
    mechanisms = [],
    footer,
    links = [],
}) {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = `${shortTitle || label.split(' · ')[0]} · Aditya Patel`;
        applyPageSeo(SEO_BY_PATH[path]);
        return () => {
            document.title = previousTitle;
            resetSeo();
        };
    }, [label, path, shortTitle]);

    return (
        <div className="course-page">
            <header className="course-hero">
                <nav className="course-nav" aria-label="Coursework navigation">
                    <a href="/">Aditya Patel</a><span>{kicker}</span>
                </nav>
                <div className="course-hero-copy">
                    <p className="course-kicker"><span /> {kicker}</p>
                    <h1>{title}</h1>
                    {lede && <p>{lede}</p>}
                </div>
                {meta.length > 0 && (
                    <div className="course-hero-meta">
                        {meta.map(([k, v]) => (
                            <div key={k}><span>{k}</span><strong>{v}</strong></div>
                        ))}
                    </div>
                )}
            </header>

            <main className="course-main">
                <section className="course-intro" aria-label="Overview">
                    {paragraphs.map(({ id, content }) => <p key={id}>{content}</p>)}
                </section>

                {pipeline.length > 0 && (
                    <section className="course-section" aria-label="System pipeline">
                        <p className="course-kicker"><span /> How it flows</p>
                        <CoursePipeline stages={pipeline} label={pipelineLabel || `${shortTitle} pipeline`} />
                    </section>
                )}

                {mechanisms.length > 0 && (
                    <section className="course-section" aria-label="Key mechanisms">
                        <p className="course-kicker"><span /> What I built</p>
                        <div className="course-mechanisms">
                            {mechanisms.map(({ name, symbols, description }) => (
                                <article key={name}>
                                    <h2>{name}</h2>
                                    <div>{symbols.map((s) => <code key={s}>{s}</code>)}</div>
                                    <p>{description}</p>
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                <section className="course-crosslink" aria-label="Featured coursework">
                    <div>
                        <p className="course-kicker"><span /> Featured coursework</p>
                        <h2>Distributed Systems Labs</h2>
                        <p>Guided protocol traces, a hands-on deterministic simulator, and a counterexample searcher — the full interactive treatment.</p>
                    </div>
                    <a href="/coursework/distributed-systems">Explore the labs →</a>
                </section>

                {links.length > 0 && (
                    <div className="course-links">
                        {links.map(({ label: linkLabel, href }) => (
                            <a
                                key={href}
                                href={href}
                                target={href.startsWith('http') ? '_blank' : undefined}
                                rel={href.startsWith('http') ? 'noreferrer' : undefined}
                            >
                                {linkLabel} {href.startsWith('http') ? '↗' : '→'}
                            </a>
                        ))}
                    </div>
                )}
                <p className="course-footer">{footer}</p>
            </main>
            <ContactSection />
        </div>
    );
}

export default CourseworkPage;
